import { Button } from '@/app/common/components/button/Button'
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
} from '@/app/common/components/Card'
import { Input } from '@/app/common/components/input/Input'
import Label from '@/app/common/components/Label'
import { ProviderPreviewSettings } from '@/schemas/provider.schema'
import { selectSelectedProgram } from '@/storage/programs/slice'
import { zodResolver } from '@hookform/resolvers/zod'
import React from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useSelector } from 'react-redux'
import { z } from 'zod'
import { useUpdatePreviewSettings } from '../hooks/useUpdatePreviewSettings'
import FormattedPhoneInput from './FormattedPhoneInput'
import AdminAuthGuard from '../../../admin-auth-guard'

// Email and phone regex patterns
const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
const phoneRegex = /^\+?[1-9]\d{1,14}$/

// Schema for Contact Information
const contactInfoSchema = z.object({
  customerService: z.object({
    email: z
      .string()
      .regex(emailRegex, 'Please enter a valid email')
      .optional()
      .or(z.literal('')),
    phone: z
      .string()
      .regex(phoneRegex, 'Please enter a valid phone number')
      .optional()
      .or(z.literal('')),
  }),
  paymentSupport: z.object({
    email: z
      .string()
      .regex(emailRegex, 'Please enter a valid email')
      .optional()
      .or(z.literal('')),
    phone: z
      .string()
      .regex(phoneRegex, 'Please enter a valid phone number')
      .optional()
      .or(z.literal('')),
  }),
})

export type ContactInfoFormData = z.infer<typeof contactInfoSchema>

interface ContactInfoCardProps {
  provider: ProviderPreviewSettings
  providerId: string
}

const ContactInfoCard: React.FC<ContactInfoCardProps> = ({
  provider,
  providerId,
}) => {
  const programId = useSelector(selectSelectedProgram).id

  const storeMutation = useUpdatePreviewSettings({
    providerId,
  })

  const form = useForm<ContactInfoFormData>({
    resolver: zodResolver(contactInfoSchema),
    defaultValues: {
      customerService: {
        email: provider.customer_service_email || '',
        phone: provider.customer_service_phone || '',
      },
      paymentSupport: {
        email: provider.payments_support_email || '',
        phone: provider.payments_support_phone || '',
      },
    },
  })

  const onSubmit = (data: ContactInfoFormData) => {
    storeMutation.mutate({
      program_id: programId,
      customer_service_email: data.customerService.email || undefined,
      customer_service_phone: data.customerService.phone || undefined,
      payments_support_email: data.paymentSupport.email || undefined,
      payments_support_phone: data.paymentSupport.phone || undefined,
    })
  }

  return (
    <Card id="contact-info">
      <CardHeader>
        <h3 className="text-lg font-semibold text-gray-900">
          Contact Information
        </h3>
      </CardHeader>
      <CardContent>
        <form id="contact-info-form" onSubmit={form.handleSubmit(onSubmit)}>
          <div className="space-y-6">
            {/* Customer Service */}
            <div className="space-y-4">
              <h5 className="text-sm font-medium text-gray-900">
                Customer Service
              </h5>
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="customerServiceEmail">Email (Optional)</Label>
                  <Input
                    {...form.register('customerService.email')}
                    id="customerServiceEmail"
                    type="email"
                    className="shadow-none"
                    placeholder="customer.service@example.com"
                  />
                  {form.formState.errors.customerService?.email && (
                    <p className="mt-1 text-sm text-red-600">
                      {form.formState.errors.customerService.email.message}
                    </p>
                  )}
                </div>
                <div>
                  <Controller
                    name="customerService.phone"
                    control={form.control}
                    render={({ field }) => (
                      <FormattedPhoneInput
                        {...field}
                        label="Phone (Optional)"
                        value={field.value}
                        onChange={field.onChange}
                        onBlur={field.onBlur}
                      />
                    )}
                  />
                  {form.formState.errors.customerService?.phone && (
                    <p className="mt-1 text-sm text-red-600">
                      {form.formState.errors.customerService.phone.message}
                    </p>
                  )}
                </div>
              </div>
            </div>
            {/* Payment Support */}
            <div className="space-y-4">
              <h5 className="text-sm font-medium text-gray-900">
                Payment Support
              </h5>
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="paymentSupportEmail">Email (Optional)</Label>
                  <Input
                    {...form.register('paymentSupport.email')}
                    id="paymentSupportEmail"
                    type="email"
                    className="shadow-none"
                    placeholder="payment.support@example.com"
                  />
                  {form.formState.errors.paymentSupport?.email && (
                    <p className="mt-1 text-sm text-red-600">
                      {form.formState.errors.paymentSupport.email.message}
                    </p>
                  )}
                </div>
                <div>
                  <Controller
                    name="paymentSupport.phone"
                    control={form.control}
                    render={({ field }) => (
                      <FormattedPhoneInput
                        {...field}
                        label="Phone (Optional)"
                        value={field.value}
                        onChange={field.onChange}
                        onBlur={field.onBlur}
                      />
                    )}
                  />
                  {form.formState.errors.paymentSupport?.phone && (
                    <p className="mt-1 text-sm text-red-600">
                      {form.formState.errors.paymentSupport.phone.message}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </form>
      </CardContent>
      <CardFooter className="flex justify-end py-4">
        <AdminAuthGuard requiredPermissions={['provider_management_edit']}>
          <Button
            type="submit"
            form="contact-info-form"
            disabled={storeMutation.isPending}
          >
            {storeMutation.isPending ? 'Saving...' : 'Save Contact Information'}
          </Button>
        </AdminAuthGuard>
      </CardFooter>
    </Card>
  )
}

export default ContactInfoCard
